if (!customElements.get('vc-header-nav')) customElements.define('vc-header-nav', class extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.header = this.closest('.shopify-section-header');
    this.bar = this.header?.querySelector('.header');
    this.logo = this.header?.querySelector('.header__heading-link--center .header__heading-logo');
    this.logo?.classList.add('vc-tactile-target');
    this.bar?.style.setProperty('--vc-nav-bar-padding', getComputedStyle(this.bar).paddingTop);
    this.sticky = this.header?.querySelector('sticky-header');
    this.surface = this.querySelector('.vc-nav-surface');
    this.sheet = this.querySelector('.vc-nav-sheet');
    this.stage = this.querySelector('.vc-nav-stage');
    this.rootView = this.querySelector('.vc-nav-view--root');
    this.panelView = this.querySelector('.vc-nav-view--submenu');
    this.shell = this.querySelector('.vc-nav-panels');
    this.root = this.querySelector('.vc-nav-root');
    this.extras = this.querySelector('.vc-nav-root-extras');
    this.panels = [...this.querySelectorAll('[data-nav-panel]')];
    this.triggers = [...this.querySelectorAll('[data-nav-trigger]')];
    this.backs = [...this.querySelectorAll('[data-nav-back]')];
    this.toggle = this.querySelector('.vc-nav-toggle');
    this.backdrop = this.querySelector('.vc-nav-backdrop');
    this.themeColor = document.querySelector('meta[name="theme-color"]');
    this.pageThemeColor = this.themeColor?.dataset.vcPageColor ?? this.themeColor?.getAttribute('content') ?? '';
    document.body.append(this.backdrop);

    this.mobile = matchMedia('(max-width:1099px)');
    this.reduced = matchMedia('(prefers-reduced-motion:reduce)');
    this.animations = [];
    this.generation = 0;
    this.keyboard = false;
    this.desiredOpen = false;
    this.requestedPanel = null;
    this.desktopPointerOpen = null;

    this.toggle.addEventListener('click', () => {
      if (['handoff-to-cart','handoff-from-cart'].includes(this.dataset.mobileState)) return;
      const cartDrawer = document.querySelector('cart-drawer.active');
      if (
        this.mobile.matches &&
        !this.desiredOpen &&
        cartDrawer?.mobileMotion?.matches &&
        typeof cartDrawer.handoffToNavigation === 'function'
      ) {
        this.openFromCartHandoff(cartDrawer);
        return;
      }
      this.desiredOpen ? this.close() : this.openRoot();
    }, {signal});
    this.triggers.forEach(button => {
      button.addEventListener('click', event => {
        if (!this.mobile.matches) {
          const name = button.dataset.navTrigger;
          const samePanelOpen = this.desiredOpen && this.dataset.panel === name;
          const initialPointerClick = Boolean(
            event.detail > 0 &&
            this.desktopPointerOpen?.name === name &&
            performance.now() - this.desktopPointerOpen.at < 250
          );
          this.desktopPointerOpen = null;
          if (samePanelOpen && !initialPointerClick) this.closeDesktop();
          else this.openDesktop(name);
          return;
        }
        this.open(button.dataset.navTrigger);
      }, {signal});
      button.addEventListener('pointerenter', event => {
        if (!this.mobile.matches && event.pointerType === 'mouse') {
          const name = button.dataset.navTrigger;
          const alreadyOpen = this.desiredOpen && this.dataset.panel === name;
          if (!alreadyOpen) this.desktopPointerOpen = {name, at:performance.now()};
          this.openDesktop(name);
        }
      }, {signal});
    });
    this.querySelector('[data-nav-direct]')?.addEventListener('pointerenter', event => {
      if (!this.mobile.matches && event.pointerType === 'mouse') this.scheduleClose();
    }, {signal});
    this.backs.forEach(back => back.addEventListener('click', () => this.openRoot(), {signal}));
    this.backdrop.addEventListener('click', () => this.close(false), {signal});
    this.querySelector('[data-cart-panel-close]')?.addEventListener('click', async () => {
      await this.close(false);
      if (!this.desiredOpen) document.querySelector('#cart-icon-bubble')?.focus({preventScroll:true});
    }, {signal});
    this.querySelectorAll('a[href]').forEach(link => link.addEventListener('click', () => this.resetImmediate(false, false), {signal}));

    this.addEventListener('pointerdown', () => {
      this.keyboard = false;
      this.sticky?.interactionIntent();
      clearTimeout(this.closeTimer);
    }, {signal});
    this.addEventListener('pointerenter', () => clearTimeout(this.closeTimer), {signal});
    this.addEventListener('pointerleave', event => {
      if (!this.mobile.matches && event.pointerType === 'mouse' && !(this.keyboard && this.contains(document.activeElement))) this.scheduleClose();
    }, {signal});
    this.addEventListener('focusin', () => clearTimeout(this.closeTimer), {signal});
    this.addEventListener('focusout', () => queueMicrotask(() => {
      if (!this.mobile.matches && !this.contains(document.activeElement) && document.activeElement !== document.querySelector('#cart-icon-bubble')) this.scheduleClose();
    }), {signal});

    document.addEventListener('pointerdown', event => {
      if (this.hasAttribute('data-open') && !this.contains(event.target) && !( !this.mobile.matches && event.target.closest('#cart-icon-bubble')) && event.target !== this.backdrop) this.close(false);
    }, {signal});
    document.addEventListener('pointermove', event => {
      if (!this.mobile.matches && this.dataset.panel === 'cart' && this.cartUpdateHold && !this.cartPendingUpdates && event.pointerType === 'mouse' && (event.movementX || event.movementY)) {
        this.cartUpdateHold = false;
        if (!this.contains(event.target) && !event.target.closest('#cart-icon-bubble')) this.scheduleClose();
      }
      if (event.pointerType === 'mouse' && event.clientY <= 8) this.sticky?.interactionIntent();
    }, {signal});
    document.addEventListener('keydown', event => {
      this.keyboard = true;
      if (event.key === 'Tab') this.cartUpdateHold = false;
      this.key(event);
    }, {signal});
    this.header?.addEventListener('pointerdown', event => {
      if (event.button > 0 || !event.target.closest('.header__heading-link--center')) return;
      if (this.mobile.matches) this.triggerPillFeedback();
      else this.triggerDesktopLogoFeedback();
    }, {signal, capture:true});
    this.header?.addEventListener('click', event => {
      if (this.mobile.matches && event.target.closest('#cart-icon-bubble,.vc-nav-toggle')) {
        this.triggerPillFeedback();
      }
      const cartIntent = event.target.closest('#cart-icon-bubble');
      const cartDrawer = cartIntent ? document.querySelector('cart-drawer') : null;
      const mobileSurfaceHandoff = Boolean(
        cartIntent &&
        this.mobile.matches &&
        cartDrawer?.mobileMotion?.matches &&
        this.hasAttribute('data-open')
      );
      if (((cartIntent && this.mobile.matches) || event.target.closest('.header__account')) && !mobileSurfaceHandoff) {
        this.resetImmediate(false, false);
      }
    }, {signal, capture:true});

    window.addEventListener('pagehide', () => {
      this.clearPillFeedback();
      this.resetImmediate(false, false);
    }, {signal});
    window.addEventListener('popstate', () => this.resetImmediate(false, true), {signal});
    window.addEventListener('pageshow', event => {
      if (event.persisted) this.resetImmediate(false, true);
    }, {signal});
    this.onModeChange = () => this.resetImmediate(false, true);
    this.mobile.addEventListener('change', this.onModeChange);

    this.ro = new ResizeObserver(() => {
      this.measure();
      if (this.hasAttribute('data-open')) this.measureHeader();
    });
    this.ro.observe(this.header);
    this.panels.forEach(panel => this.ro.observe(panel));
    this.resetImmediate(false, true);
  }

  disconnectedCallback() {
    this.clearPillFeedback();
    this.resetImmediate(false, false);
    this.abort?.abort();
    this.ro?.disconnect();
    this.mobile?.removeEventListener('change', this.onModeChange);
    this.backdrop?.remove();
  }

  setState(state) {
    this.dataset.mobileState = state;
  }

  triggerPillFeedback() {
    if (!this.mobile.matches) return;
    window.VelveleTactileFeedback?.play(this.bar);
  }

  triggerDesktopLogoFeedback() {
    if (this.mobile.matches) return;
    window.VelveleTactileFeedback?.play(this.logo);
  }

  clearPillFeedback() {
    window.VelveleTactileFeedback?.clear(this.bar);
    window.VelveleTactileFeedback?.clear(this.logo);
  }

  setControlIntent(expanded) {
    this.toggle?.setAttribute('aria-expanded', String(expanded));
    this.toggle?.setAttribute('aria-label', expanded ? 'Close navigation' : 'Open navigation');
  }

  setBrowserChromeSurface(navigationOpen) {
    const cartOpen = document.querySelector('cart-drawer.active, cart-drawer.vc-cart-closing');
    const surfaceOpen = this.mobile.matches && (navigationOpen || Boolean(cartOpen));
    document.documentElement.classList.toggle('vc-mobile-surface-open', surfaceOpen);
    this.themeColor?.setAttribute('content', surfaceOpen ? '#ffffff' : this.pageThemeColor);
  }

  cancelWork() {
    clearTimeout(this.closeTimer);
    clearTimeout(this.chromePreArmTimer);
    this.chromePreArmTimer = 0;
    const token = ++this.generation;
    this.clearAnimations();
    return token;
  }

  clearAnimations() {
    this.animations.forEach(animation => animation.cancel());
    this.animations = [];
  }

  runAnimation(element, frames, options = {}) {
    if (this.reduced.matches || !element || element.hidden) return Promise.resolve();
    const animation = element.animate(frames, {
      duration: options.duration ?? 220,
      delay: options.delay ?? 0,
      easing: options.easing ?? 'cubic-bezier(.4,0,.2,1)',
      fill: 'both'
    });
    this.animations.push(animation);
    return animation.finished.catch(() => undefined);
  }

  visibleItems(view) {
    if (!view) return [];
    return [...view.querySelectorAll('a[href],button,p')].filter(element => {
      const blocker = element.closest('[hidden],[inert]');
      return (!blocker || blocker === view) && element.getClientRects().length;
    });
  }

  animateCascade(view, {duration = 230, delay = 0, stagger = 17} = {}) {
    return this.animateItems(this.visibleItems(view), {duration, delay, stagger});
  }

  animateItems(elements, {duration = 230, delay = 0, stagger = 17} = {}) {
    return Promise.all(elements.map((element, index) => this.runAnimation(
      element,
      [{opacity:0}, {opacity:1}],
      {
        duration,
        delay: delay + index * stagger,
        easing:'linear'
      }
    )));
  }

  animateLayer(element, entering, {duration, delay = 0} = {}) {
    return this.runAnimation(
      element,
      entering ? [{opacity:0}, {opacity:1}] : [{opacity:1}, {opacity:0}],
      {
        duration,
        delay,
        easing:'cubic-bezier(.2,0,.8,1)'
      }
    );
  }

  animateStage(element, entering, {duration, delay = 0, x} = {}) {
    const rest = 'translate3d(0,0,0)';
    const offset = `translate3d(${x}px,0,0)`;
    return this.runAnimation(
      element,
      entering
        ? [{opacity:0, transform:offset}, {opacity:1, transform:rest}]
        : [{opacity:1, transform:rest}, {opacity:0, transform:offset}],
      {
        duration,
        delay,
        easing:'cubic-bezier(.2,0,.8,1)'
      }
    );
  }

  animateItemsOut(elements, {duration = 160} = {}) {
    return Promise.all(elements.map(element => this.runAnimation(
      element,
      [{opacity:getComputedStyle(element).opacity}, {opacity:0}],
      {
        duration,
        easing:'cubic-bezier(.2,0,.8,1)'
      }
    )));
  }

  wait(duration) {
    return new Promise(resolve => setTimeout(resolve, this.reduced.matches ? 0 : duration));
  }

  nextFrame() {
    return new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  }

  measureHeader() {
    const height = this.header?.getBoundingClientRect().height || 80;
    this.style.setProperty('--nav-header-height', `${height}px`);
    this.backdrop.style.setProperty('--nav-header-height', `${height}px`);
  }

  measure() {
    if (!this.mobile.matches && this.bar) {
      const style = getComputedStyle(this.bar);
      const width = this.bar.getBoundingClientRect().width - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      this.style.setProperty('--vc-cart-x', `${width / 48}px`);
    }
    const panel = this.querySelector('[data-nav-panel]:not([hidden])');
    if (panel?.dataset.navPanel === 'cart' && !this.mobile.matches) {
      panel.querySelectorAll('.cart-item').forEach(item => {
        const title = item.querySelector('.cart-item__line-group');
        const remove = item.querySelector('cart-remove-button');
        if (title && remove && remove.parentNode !== title) title.append(remove);
      });
      const sizes = [...panel.querySelectorAll('.cart-item')].map(item => {
        const details = item.querySelector('.cart-item__details');
        const width = details ? details.getBoundingClientRect().height * 1.1 * 136 / 166 : 0;
        return {item, width};
      });
      sizes.forEach(({item, width}) => {
        const previous = parseFloat(item.style.getPropertyValue('--vc-cart-thumbnail-width')) || 0;
        if (width > 0 && Math.abs(previous - width) > 0.1) item.style.setProperty('--vc-cart-thumbnail-width', `${width.toFixed(2)}px`);
      });
      const firstImage = panel.querySelector('.cart-item__media');
      const grid = panel.querySelector('.drawer__inner');
      if (firstImage && grid) this.style.setProperty('--vc-cart-summary-offset', `${(firstImage.getBoundingClientRect().top - grid.getBoundingClientRect().top).toFixed(2)}px`);
    }
    if (panel && !this.mobile.matches) {
      const height = `${panel.offsetHeight}px`;
      this.shell.style.setProperty('--nav-panel-height', height);
      this.header?.style.setProperty('--vc-nav-panel-height', height);
    }
  }

  acquireLock({preserveCart = false} = {}) {
    if (!preserveCart && this.mobile.matches) document.querySelector('cart-drawer.active')?.close();
    if (!this.saved) {
      this.saved = {
        x: scrollX,
        y: scrollY,
        html: document.documentElement.style.overflow,
        body: document.body.style.overflow,
        mobile: this.mobile.matches
      };
      if (!this.saved.mobile) {
        this.placeholder = document.createElement('div');
        this.placeholder.setAttribute('aria-hidden', 'true');
        this.placeholder.style.height = `${this.header.getBoundingClientRect().height}px`;
        this.header.before(this.placeholder);
      }
      this.background = [...document.querySelectorAll('main,footer')].map(element => ({element, inert:element.inert}));
      this.background.forEach(({element}) => element.inert = true);
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.classList.add('vc-nav-lock');
    }
    this.desiredOpen = true;
    this.setControlIntent(true);
    if (this.mobile.matches) this.setBrowserChromeSurface(true);
    this.setAttribute('data-open', '');
    this.header?.classList.remove('vc-nav-resetting');
    this.header?.classList.add('vc-nav-open');
    this.sticky?.interactionIntent();
    this.surface.inert = false;
    this.surface.setAttribute('aria-hidden', 'false');
    this.measureHeader();
    this.backdrop.hidden = this.mobile.matches;
  }

  releaseLock(restoreScroll = true) {
    const restoreMobileChrome = this.saved?.mobile || this.mobile.matches;
    this.placeholder?.remove();
    this.placeholder = null;
    this.background?.forEach(({element, inert}) => element.inert = inert);
    this.background = null;
    if (this.saved) {
      const saved = this.saved;
      this.saved = null;
      document.documentElement.style.overflow = saved.html;
      document.body.style.overflow = saved.body;
      document.body.classList.remove('vc-nav-lock');
      if (restoreScroll && !saved.mobile) window.scrollTo(saved.x, saved.y);
    }
    if (restoreMobileChrome) this.setBrowserChromeSurface(false);
  }

  setTriggerState(name) {
    this.triggers.forEach(button => button.setAttribute('aria-expanded', String(button.dataset.navTrigger === name)));
  }

  selectPanel(name) {
    this.panels.forEach(panel => {
      panel.hidden = panel.dataset.navPanel !== name;
      panel.inert = panel.hidden;
      const back = panel.querySelector('[data-nav-back]');
      if (back) back.hidden = !this.mobile.matches || panel.hidden;
    });
  }

  commitMobileView(name) {
    this.requestedPanel = name;
    if (name) this.dataset.panel = name;
    else delete this.dataset.panel;
    this.rootView.hidden = !!name;
    this.rootView.inert = !!name;
    this.panelView.hidden = !name;
    this.panelView.inert = !name;
    this.selectPanel(name);
    this.setTriggerState(name);
    const active = name ? this.panels.find(panel => panel.dataset.navPanel === name) : this.rootView;
    if (active) active.scrollTop = 0;
  }

  prepareMobileTransition(fromName, toName) {
    const panelName = toName || fromName;
    this.rootView.hidden = false;
    this.rootView.inert = true;
    this.panelView.hidden = false;
    this.panelView.inert = true;
    this.selectPanel(panelName);
    this.setTriggerState(toName);
    return this.panels.find(panel => panel.dataset.navPanel === panelName);
  }

  setDesktopView(name) {
    if (name !== 'cart') this.cartUpdateHold = false;
    if (name) this.dataset.panel = name;
    else delete this.dataset.panel;
    this.requestedPanel = name;
    this.rootView.hidden = false;
    this.rootView.inert = false;
    this.root.hidden = false;
    this.root.inert = false;
    this.extras.hidden = true;
    this.extras.inert = true;
    this.shell.hidden = !name;
    this.shell.inert = !name;
    this.selectPanel(name);
    this.setTriggerState(name);
    const cart = this.querySelector('cart-drawer');
    if (cart) {
      cart.classList.toggle('active', name === 'cart');
      cart.setCartControlExpanded(name === 'cart');
    }
  }

  openRoot() {
    return this.open(null);
  }

  open(name) {
    if (!this.mobile.matches) return name ? this.openDesktop(name) : this.closeDesktop();
    if (!this.hasAttribute('data-open')) return this.openMobileRoot();
    if ((this.requestedPanel || null) === name && !['forward','back','closing'].includes(this.dataset.mobileState)) return;
    if (this.dataset.mobileState === 'closing' && !name) return this.openMobileRoot();
    return this.transitionMobile(name);
  }

  async openMobileRoot() {
    const openingFromClose = this.dataset.mobileState === 'closing';
    const sheetStart = openingFromClose ? getComputedStyle(this.sheet).clipPath : 'inset(0 0 100% 0)';
    const token = this.cancelWork();
    if (openingFromClose) this.sheet.style.clipPath = sheetStart;
    this.requestedPanel = null;
    this.acquireLock();
    this.setState('opening-root');
    this.commitMobileView(null);
    this.rootView.inert = true;
    const primary = [...this.root.children].filter(element => element.getClientRects().length);
    const secondary = [...this.extras.querySelectorAll('p,a[href],button')]
      .filter(element => element.getClientRects().length);
    const primaryDelay = 180;
    const primaryStagger = 40;
    const secondaryDelay = primaryDelay + Math.max(primary.length - 1, 0) * primaryStagger + 105;
    await Promise.all([
      this.runAnimation(this.sheet, [{clipPath:sheetStart}, {clipPath:'inset(0 0 0% 0)'}], {
        duration:360,
        easing:'cubic-bezier(.4,0,.2,1)'
      }),
      this.animateItems(primary, {duration:240, delay:primaryDelay, stagger:primaryStagger}),
      this.animateItems(secondary, {duration:220, delay:secondaryDelay, stagger:40})
    ]);
    if (token !== this.generation) return;
    this.rootView.inert = false;
    this.setState('root');
    this.sheet.style.removeProperty('clip-path');
    this.clearAnimations();
    if (this.keyboard) this.triggers[0]?.focus({preventScroll:true});
  }

  async transitionMobile(name) {
    const fromName = this.requestedPanel || null;
    const openingRoot = this.dataset.mobileState === 'opening-root';
    const token = this.cancelWork();
    if (openingRoot) this.sheet.style.clipPath = 'inset(0 0 0% 0)';
    const fromView = fromName ? this.panelView : this.rootView;
    const panel = this.prepareMobileTransition(fromName, name);
    const toView = name ? this.panelView : this.rootView;
    this.requestedPanel = name;
    this.setState(name ? 'forward' : 'back');
    const outgoingX = name ? -7 : 7;
    const incomingX = name ? 7 : -7;
    await Promise.all([
      this.animateStage(fromView, false, {duration:160, x:outgoingX}),
      this.animateStage(toView, true, {duration:200, delay:160, x:incomingX})
    ]);
    if (token !== this.generation) return;
    this.commitMobileView(name);
    this.surface.inert = false;
    this.setState(name ? 'submenu' : 'root');
    this.clearAnimations();
    if (this.keyboard) {
      if (name) panel?.querySelector('[data-nav-back]')?.focus({preventScroll:true});
      else this.querySelector(`[data-nav-trigger="${fromName || 'store'}"]`)?.focus({preventScroll:true});
    }
  }

  async handoffToCart(cartDrawer, triggeredBy) {
    if (
      !this.mobile.matches ||
      !cartDrawer?.mobileMotion?.matches ||
      !this.hasAttribute('data-open')
    ) {
      cartDrawer?.open(triggeredBy);
      return;
    }
    if (['handoff-to-cart','handoff-from-cart'].includes(this.dataset.mobileState)) return;

    const previous = this.requestedPanel;
    this.desiredOpen = false;
    this.setControlIntent(false);
    const active = previous ? this.panels.find(panel => panel.dataset.navPanel === previous) : this.rootView;
    const activeItems = [...this.stage.querySelectorAll('a[href],button,p')]
      .filter(element => element.getClientRects().length);
    const token = this.cancelWork();
    this.sheet.style.clipPath = 'inset(0 0 0 0)';
    this.setState('handoff-to-cart');
    if (active) active.inert = true;

    if (!this.reduced.matches) {
      await (activeItems.length
        ? this.animateItemsOut(activeItems, {duration:160})
        : this.animateLayer(active, false, {duration:160}));
    }
    if (token !== this.generation) return;

    cartDrawer.open(triggeredBy, {surfaceHandoff:true});
    await this.nextFrame();
    if (token !== this.generation) return;

    this.removeAttribute('data-open');
    this.header?.classList.remove('vc-nav-open');
    this.commitMobileView(null);
    this.surface.inert = true;
    this.surface.setAttribute('aria-hidden', 'true');
    this.setState('closed');
    this.sheet.style.removeProperty('clip-path');
    this.backdrop.hidden = true;
    this.releaseLock(true);
    this.clearAnimations();
    this.sticky?.interactionIntent();
  }

  async openFromCartHandoff(cartDrawer) {
    if (
      !this.mobile.matches ||
      !cartDrawer?.mobileMotion?.matches ||
      !cartDrawer.classList.contains('active')
    ) {
      this.openRoot();
      return;
    }
    if (['handoff-to-cart','handoff-from-cart'].includes(this.dataset.mobileState)) return;

    const token = this.cancelWork();
    this.requestedPanel = null;
    this.setControlIntent(true);
    this.setState('handoff-from-cart');
    this.commitMobileView(null);
    this.rootView.inert = true;
    this.sheet.style.clipPath = 'inset(0 0 0 0)';
    const primary = [...this.root.children].filter(element => element.getClientRects().length);
    const secondary = [...this.extras.querySelectorAll('p,a[href],button')]
      .filter(element => element.getClientRects().length);
    const revealItems = [...primary, ...secondary];
    if (!this.reduced.matches) revealItems.forEach(element => element.style.opacity = '0');

    const cartReleased = await cartDrawer.handoffToNavigation(() => {
      if (token !== this.generation) return false;
      this.acquireLock({preserveCart:true});
      this.setState('handoff-from-cart');
      return true;
    });
    if (token !== this.generation || !cartReleased) {
      if (!this.hasAttribute('data-open')) this.setControlIntent(false);
      revealItems.forEach(element => element.style.removeProperty('opacity'));
      return;
    }

    const primaryStagger = 40;
    const secondaryDelay = Math.max(primary.length - 1, 0) * primaryStagger + 105;
    this.setState('opening-root');
    await Promise.all([
      this.animateItems(primary, {duration:240, delay:0, stagger:primaryStagger}),
      this.animateItems(secondary, {duration:220, delay:secondaryDelay, stagger:40})
    ]);
    if (token !== this.generation) {
      revealItems.forEach(element => element.style.removeProperty('opacity'));
      return;
    }

    revealItems.forEach(element => element.style.removeProperty('opacity'));
    this.rootView.inert = false;
    this.setState('root');
    this.sheet.style.removeProperty('clip-path');
    this.clearAnimations();
    if (this.keyboard) this.triggers[0]?.focus({preventScroll:true});
  }

  async openDesktop(name) {
    if (this.desiredOpen && this.dataset.panel === name) return;
    const token = this.cancelWork();
    const wasOpen = this.hasAttribute('data-open');
    this.acquireLock();
    this.setDesktopView(name);
    this.measure();
    await Promise.all([
      this.runAnimation(this.shell, [{opacity:0, transform:'translateY(-10px)'}, {opacity:1, transform:'translateY(0)'}], {duration:200}),
      !wasOpen ? this.runAnimation(this.backdrop, [{opacity:0}, {opacity:1}], {duration:240}) : Promise.resolve()
    ]);
    if (token !== this.generation) return;
    this.clearAnimations();
    if (this.keyboard) {
      if (name === 'cart') this.querySelector('[data-nav-panel="cart"] .cart-item__name')?.focus({preventScroll:true});
      else this.querySelector(`[data-nav-trigger="${name}"]`)?.focus({preventScroll:true});
    }
  }

  close(returnFocus = true, immediate = false) {
    return this.mobile.matches ? this.closeMobile(returnFocus, immediate) : this.closeDesktop(returnFocus, immediate);
  }

  async closeMobile(returnFocus = true, immediate = false) {
    const wasOpen = this.hasAttribute('data-open');
    if (!wasOpen && !this.saved) return this.resetImmediate(returnFocus, true);
    const previous = this.requestedPanel;
    this.desiredOpen = false;
    this.setControlIntent(false);
    const sheetStart = getComputedStyle(this.sheet).clipPath;
    const active = previous ? this.panels.find(panel => panel.dataset.navPanel === previous) : this.rootView;
    const activeItems = [...this.stage.querySelectorAll('a[href],button,p')]
      .filter(element => element.getClientRects().length);
    const token = this.cancelWork();
    this.sheet.style.clipPath = sheetStart;
    this.setState('closing');
    if (active) active.inert = true;
    if (!immediate && !this.reduced.matches) {
      const closeTerminal = 160 + 350;
      const t3Lead = 200;
      this.chromePreArmTimer = setTimeout(() => {
        if (token === this.generation && !this.desiredOpen && this.dataset.mobileState === 'closing') {
          this.setBrowserChromeSurface(false);
        }
      }, closeTerminal - t3Lead);
    }
    if (!immediate) await Promise.all([
      activeItems.length ? this.animateItemsOut(activeItems, {duration:160}) : this.animateLayer(active, false, {duration:160}),
      this.runAnimation(this.sheet, [{clipPath:sheetStart}, {clipPath:'inset(0 0 100% 0)'}], {
        duration:350,
        delay:160,
        easing:'cubic-bezier(.4,0,.2,1)'
      })
    ]);
    if (token !== this.generation) return;
    clearTimeout(this.chromePreArmTimer);
    this.chromePreArmTimer = 0;
    this.removeAttribute('data-open');
    this.header?.classList.remove('vc-nav-open');
    this.commitMobileView(null);
    this.surface.inert = true;
    this.surface.setAttribute('aria-hidden', 'true');
    this.setState('closed');
    this.sheet.style.removeProperty('clip-path');
    this.backdrop.hidden = true;
    this.releaseLock(true);
    this.clearAnimations();
    this.sticky?.interactionIntent();
    if (scrollY === 0) this.sticky?.reset();
    if (wasOpen && returnFocus && this.keyboard) this.toggle.focus({preventScroll:true});
  }

  async closeDesktop(returnFocus = true, immediate = false) {
    const wasOpen = this.hasAttribute('data-open');
    const previous = this.dataset.panel;
    if (!wasOpen && !this.saved) return;
    this.desiredOpen = false;
    this.setControlIntent(false);
    const token = this.cancelWork();
    this.shell.inert = true;
    this.shell.style.setProperty('--nav-panel-height', '0px');
    this.header?.style.setProperty('--vc-nav-panel-height', '0px');
    if (!immediate) await Promise.all([
      this.runAnimation(this.shell, [{opacity:1, transform:'translateY(0)'}, {opacity:0, transform:'translateY(-10px)'}], {duration:200}),
      this.runAnimation(this.backdrop, [{opacity:1}, {opacity:0}], {duration:240})
    ]);
    if (token !== this.generation) return;
    this.removeAttribute('data-open');
    this.header?.classList.remove('vc-nav-open');
    this.setDesktopView(null);
    this.backdrop.hidden = true;
    this.releaseLock(true);
    this.clearAnimations();
    if (wasOpen && returnFocus && this.keyboard) {
      const trigger = previous === 'cart' ? document.querySelector('#cart-icon-bubble') : this.querySelector(`[data-nav-trigger="${previous || 'store'}"]`);
      trigger?.focus({preventScroll:true});
    }
  }

  resetImmediate(returnFocus = false, restoreScroll = true) {
    const wasOpen = this.hasAttribute('data-open');
    const previous = this.dataset.panel;
    this.desiredOpen = false;
    this.requestedPanel = null;
    const token = this.cancelWork();
    this.header?.classList.add('vc-nav-resetting');
    this.removeAttribute('data-open');
    this.header?.classList.remove('vc-nav-open');
    this.setControlIntent(false);
    this.backdrop.hidden = true;
    if (this.mobile.matches) {
      this.commitMobileView(null);
      this.surface.inert = true;
      this.surface.setAttribute('aria-hidden', 'true');
    } else {
      this.setDesktopView(null);
      this.surface.inert = false;
      this.surface.removeAttribute('aria-hidden');
    }
    this.setState('closed');
    this.releaseLock(restoreScroll);
    this.shell.style.removeProperty('--nav-panel-height');
    this.header?.style.removeProperty('--vc-nav-panel-height');
    requestAnimationFrame(() => {
      if (token === this.generation) this.header?.classList.remove('vc-nav-resetting');
    });
    this.sticky?.interactionIntent();
    if (wasOpen && returnFocus && this.keyboard) {
      (this.mobile.matches ? this.toggle : this.querySelector(`[data-nav-trigger="${previous || 'store'}"]`))?.focus({preventScroll:true});
    }
  }

  beginCartUpdate() {
    this.cartPendingUpdates = (this.cartPendingUpdates || 0) + 1;
    this.cartUpdateHold = true;
    clearTimeout(this.closeTimer);
  }

  finishCartUpdate() {
    this.cartPendingUpdates = Math.max(0, (this.cartPendingUpdates || 0) - 1);
    clearTimeout(this.closeTimer);
    this.cartUpdateHold = this.dataset.panel === 'cart';
  }

  scheduleClose() {
    clearTimeout(this.closeTimer);
    if (this.dataset.panel === 'cart' && (this.cartPendingUpdates || this.cartUpdateHold)) return;
    this.closeTimer = setTimeout(() => this.close(false), 170);
  }

  key(event) {
    if (event.key === 'Escape' && this.hasAttribute('data-open')) {
      event.preventDefault();
      this.close();
    }
    if (event.key !== 'Tab' || !this.mobile.matches || !this.hasAttribute('data-open')) return;
    const focusable = [...this.querySelectorAll('a[href],button')].filter(element => !element.closest('[hidden],[inert]') && element.getClientRects().length);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  }
});

